import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

export const PWAInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Détecter iOS
    const isIOSDevice =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);

    // Vérifier si l'app est déjà installée
    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    const isInWebAppiOS = (window.navigator as any).standalone === true;
    const alreadyInstalled = standalone || isInWebAppiOS;

    setIsStandalone(standalone);
    setIsInstalled(alreadyInstalled);

    // Afficher immédiatement le prompt si possible
    const showPromptImmediately = () => {
      // Vérifier si on est sur mobile ou tablette
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

      if (isMobile && !alreadyInstalled) {
        setShowInstallPrompt(true);
      }
    };

    // Écouter l'événement beforeinstallprompt
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Ne pas définir showInstallPrompt ici, on le fait dans showPromptImmediately
    };

    // Écouter l'événement appinstalled
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    };

    // Afficher le prompt après un court délai pour laisser le temps au chargement
    const timer = setTimeout(showPromptImmediately, 1500);

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    // Si sur iOS, on affiche les instructions d'installation
    if (isIOS) {
      setShowInstallPrompt(true);
      return;
    }

    // Pour les autres appareils, on utilise le prompt d'installation natif
    if (!deferredPrompt) {
      console.warn("L'événement d'installation n'est pas disponible");
      return;
    }

    try {
      // Afficher le prompt d'installation natif
      deferredPrompt.prompt();

      // Attendre que l'utilisateur réponde au prompt
      const { outcome } = await deferredPrompt.userChoice;

      console.log(`Résultat de l'installation: ${outcome}`);

      if (outcome === 'accepted') {
        console.log("L'utilisateur a accepté l'installation");
      } else {
        console.log("L'utilisateur a refusé l'installation");
      }

      // Réinitialiser le prompt
      setDeferredPrompt(null);
    } catch (error) {
      console.error("Erreur lors de l'installation de la PWA:", error);

      // En cas d'erreur, afficher quand même les instructions pour iOS
      if (isIOS) {
        setShowInstallPrompt(true);
      }
    } finally {
      // Toujours masquer le prompt après l'interaction
      setShowInstallPrompt(false);
    }
  };

  const handleDismissInstall = () => {
    setShowInstallPrompt(false);
    setDeferredPrompt(null);
  };

  const handleDismissUpdate = () => {
    setShowUpdatePrompt(false);
  };

  if (isInstalled) {
    return null;
  }

  // Rendu des instructions d'installation pour iOS
  const renderIOSInstallInstructions = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Install Datasset</h3>
          <button onClick={handleDismissInstall} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <p className="text-gray-700">To install this app on your iPhone:</p>

          <div className="flex items-start space-x-3">
            <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
              1
            </span>
            <div>
              <p className="font-medium">Tap the Share button</p>
              <p className="text-sm text-gray-600">It's the square with an arrow pointing up</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
              2
            </span>
            <div>
              <p className="font-medium">Select "Add to Home Screen"</p>
              <p className="text-sm text-gray-600">Scroll down if you don't see it immediately</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
              3
            </span>
            <div>
              <p className="font-medium">Confirm the installation</p>
              <p className="text-sm text-gray-600">Tap "Add" in the top-right corner</p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleDismissInstall}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );

  // Rendu du prompt d'installation standard
  const renderStandardInstallPrompt = () => (
    <div className="fixed bottom-20 left-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 animate-slide-up">
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Download className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-text-principal">Install Datasset</h3>
            <p className="text-sm text-mist-grey">
              Add the app to your home screen for quick access
            </p>
          </div>
        </div>
        <button
          onClick={handleDismissInstall}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="w-4 h-4 text-mist-grey" />
        </button>
      </div>
      <div className="flex space-x-2 mt-3">
        <button
          onClick={handleInstallClick}
          className="flex-1 bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-dark transition-colors"
        >
          Install
        </button>
        <button
          onClick={handleDismissInstall}
          className="px-4 py-2 text-mist-grey hover:bg-gray-100 rounded-lg transition-colors"
        >
          Later
        </button>
      </div>
    </div>
  );

  return (
    <>
      {showInstallPrompt && (
        <>{isIOS ? renderIOSInstallInstructions() : renderStandardInstallPrompt()}</>
      )}
    </>
  );
};

